import React, { useCallback, useMemo, useRef, useState } from 'react';
import { type NodeProps } from '@xyflow/react';
import type { PeoplePickerPerson } from '@aibuild/ui';
import type { AssigneeType, ProcessArtifactDto } from '@api/processes';
import type {
  ArtifactsPanelStyle,
  BranchDto,
  ConfigFieldDto,
  ConfigFieldOptionDto,
  NodeDefinitionDto,
} from '@api/nodeDefinitions';
import { TASK_AUTO_SKILL_ID } from '../graphTypes';
import type { Port, ProcessNode as ProcessNodeRfType } from '../graphTypes';
import ConfigFieldRenderer from './ConfigFieldRenderer';
import CapabilityHint from './CapabilityHint';
import NodeSection from './NodeSection';
import BranchRow from './BranchRow';
import OutputRow from './OutputRow';
import ArtifactsPanel from './ArtifactsPanel';
import ImageGenNodeBody from './ImageGenNodeBody';
import NodeHeaderRunButton from './NodeHeaderRunButton';
import NodeQuickMenu from './NodeQuickMenu';
import NodeFloatingActions from './NodeFloatingActions';
import { HeaderEndCluster, RenameInput, RunStatsRow } from './ProcessNode.styled';
import { useProcessNodeActionHandlers } from './useProcessNodeActionHandlers';
import { useProcessNodeDerivedState } from './useProcessNodeDerivedState';
import { useNodeLiveArtifacts } from './useNodeLiveArtifacts';
import { useEditorContext } from '../useEditorContext';
import { useOrgMembers } from '../../hooks/useOrgMembers';
import { useGroups } from '../../hooks/useGroups';
import { useCapabilityCoverage } from '../../hooks/useCapabilityCoverage';
import { resolveGateRunSubtitle } from './resolveGateRunSubtitle';
import { resolveNodeRunStats } from './resolveNodeRunStats';
import {
  requiredAppsForSkills,
  resolveOutputKindOptions,
  stringList,
} from '../taskSkillSelection';

interface HeroSize {
  width: number;
  height: number;
}

/**
 * Skill dropdown options for the current execution target. The backend
 * ships them per target on `NodeDefinitionDto.agentOptionsByTarget`
 * (`CLOUD` / `LOCAL`) so the cloud and local skill catalogs stay in
 * lockstep with the runtime allocator. Returns undefined when the map is
 * absent (every non-Task definition) so callers leave the field's own
 * options untouched.
 */
export function resolveTaskSkillOptions(
  definition: NodeDefinitionDto | undefined,
  runnerTargetRaw: unknown,
): readonly ConfigFieldOptionDto[] | undefined {
  const map = definition?.agentOptionsByTarget;
  if (!map) return undefined;
  const target = runnerTargetRaw === 'LOCAL' ? 'LOCAL' : 'CLOUD';
  return map[target] ?? undefined;
}

/** Namespace the backend gives an organization's own skills. */
const CUSTOM_SKILL_PREFIX = 'custom:';

/**
 * Keep saved organization skills visible in the dropdown after they were
 * deleted from the Skills page. Without an entry the picker falls back to its
 * "Select a skill…" placeholder, which reads as "never configured" — the node
 * looks untouched while validation refuses to publish it.
 */
function withDeletedSkillOptions(
  options: readonly ConfigFieldOptionDto[],
  selected: readonly string[],
): readonly ConfigFieldOptionDto[] {
  const missing = selected.filter(
    (id) => id.startsWith(CUSTOM_SKILL_PREFIX) && !options.some((o) => o.value === id),
  );
  if (missing.length === 0) return options;
  return [
    ...options,
    ...missing.map((id) => ({
      value: id,
      label: 'Deleted skill',
      description:
        'This custom skill was removed from your organization. Pick another skill to run this task.',
      // Matches the live custom options the server sends; without it the picker
      // would let a deleted skill be combined where a present one cannot be.
      exclusive: true,
    })),
  ];
}

/**
 * Swap the `skillIds` field's options to the target-specific skill catalog.
 * No-op when the definition ships no per-target map (non-Task nodes).
 */
export function applyTaskSkillOptions(
  fields: readonly ConfigFieldDto[],
  options: readonly ConfigFieldOptionDto[] | undefined,
  selected: readonly string[] = [],
): readonly ConfigFieldDto[] {
  if (!options) return fields;
  const withFallback = withDeletedSkillOptions(options, selected);
  return fields.map((f) =>
    f.key === 'skillIds' && f.type === 'MULTI_SELECT'
      ? { ...f, options: withFallback }
      : f,
  );
}

/**
 * Null when the selection survives a flip to `nextTarget` untouched. Otherwise
 * drops only the ids the new catalog does not offer, falling back to Auto —
 * which both catalogs offer — when that leaves nothing.
 */
export function retainSkillIdsForTarget(
  definition: NodeDefinitionDto | undefined,
  nextTarget: unknown,
  current: readonly string[],
): string[] | null {
  if (current.length === 0) return null;
  const options = resolveTaskSkillOptions(definition, nextTarget);
  if (!options) return null;
  const kept = current.filter((id) => options.some((o) => o.value === id));
  if (kept.length === current.length) return null;
  return kept.length > 0 ? kept : [TASK_AUTO_SKILL_ID];
}

/** Agent-executing kinds — the only ones with billed run stats to show. */
const RUN_STATS_KINDS: ReadonlySet<string> = new Set([
  'task',
  'gate',
  'image_gen',
  '3d_gen',
]);

/** Horizontal padding inside NodeCardShell.Body (18px × 2). */
const IMAGE_GEN_CARD_PADDING = 36;
/** Default card width for an unresized image_gen node. */
const IMAGE_GEN_CARD_DEFAULT_WIDTH = 300;

/**
 * Narrow an unknown dataBag value to a HeroSize, returning a memo-stable
 * reference so downstream useCallback deps don't churn on every render.
 */
const useHeroSizeMemo = (raw: unknown): HeroSize | null =>
  useMemo(() => {
    if (
      raw !== null &&
      typeof raw === 'object' &&
      typeof (raw as { width?: unknown }).width === 'number' &&
      typeof (raw as { height?: unknown }).height === 'number'
    ) {
      return raw as HeroSize;
    }
    return null;
  }, [raw]);

/**
 * Card width for an image_gen node. Returns null for every other kind
 * (which falls back to React Flow's catalog default width).
 */
const computeImageGenNodeWidth = (
  isImageGen: boolean,
  liveSize: HeroSize | null,
  heroSize: HeroSize | null,
): number | null => {
  if (!isImageGen) return null;
  const effective = liveSize ?? heroSize;
  if (!effective) return IMAGE_GEN_CARD_DEFAULT_WIDTH;
  return effective.width + IMAGE_GEN_CARD_PADDING;
};

interface ImageGenBodyParams {
  nodeId: string;
  instruction: string;
  onInstructionChange: (val: string) => void;
  latestArtifacts: readonly ProcessArtifactDto[] | undefined;
  outputPort: Port | undefined;
  heroSize: HeroSize | null;
  heroMinSize: HeroSize | null;
  liveSize: HeroSize | null;
  onLiveSizeChange: (size: HeroSize | null) => void;
  onHeroSizeChange: (size: HeroSize, minSize: HeroSize) => void;
  genModel: string | undefined;
  editModel: string | undefined;
  onModelChange: (field: 'genModel' | 'editModel', value: string) => void;
  readOnly: boolean;
}

/**
 * Task config-field keys grouped under the collapsible "Execution" section.
 * Everything else (agent, model, instruction) stays inline above it. Keys not
 * present in a given definition's schema, or hidden by `visibleWhen` (e.g. the
 * assignee/timeout fields on a CLOUD task), simply drop out.
 */
const TASK_EXECUTION_FIELD_KEYS: ReadonlySet<string> = new Set([
  'runnerTarget',
  'assigneeType',
  'assigneeIds',
  'assigneeGroupId',
  'timeoutHours',
  'maxCredits',
]);

interface GenericBodyParams {
  nodeId: string;
  fields: readonly ConfigFieldDto[];
  dataBag: Record<string, unknown>;
  onFieldChange: (key: string, value: unknown) => void;
  readOnly: boolean;
  people: readonly PeoplePickerPerson[];
  groups: readonly { id: string; name: string }[];
  groupsLoaded: boolean;
  latestArtifacts: readonly ProcessArtifactDto[] | undefined;
  artifactsStyle: ArtifactsPanelStyle;
  showOutputRow: boolean;
  outputPortIds: ReadonlySet<string>;
  generationPreview: boolean;
  extraSlot: React.ReactNode;
  /** When set, fields whose key is in this set are grouped into a collapsed
   *  "Execution" section (task nodes); the rest render inline above it. */
  executionSectionKeys?: ReadonlySet<string>;
  /** Small dimmed value shown next to the "Execution" section title. */
  executionSubtitle?: React.ReactNode;
}

interface FloatingSlotsArgs {
  nodeId: string;
  isComputable: boolean;
  showHeaderRunControl: boolean;
  editable: boolean;
  isOnlyFloatingHost: boolean;
  branches: readonly BranchDto[];
  ports: readonly Port[] | undefined;
}

interface FloatingSlots {
  headerEnd: React.ReactNode;
  floatingActions: React.ReactNode;
  branchesSlot: React.ReactNode;
}

/**
 * Compute the three header-adjacent JSX slots that don't depend on the
 * rename ref (header end-cluster, floating actions, branches strip).
 * Extracted as a plain helper so the host hook's cognitive-complexity
 * budget isn't dominated by these tiny ternaries.
 *
 * The {@code actions} slot stays inline at the call site because its
 * {@code onRenameRequest} callback reads {@code renameRef.current},
 * and react-hooks/refs flags refs threaded through helpers.
 */
const buildFloatingSlots = (p: FloatingSlotsArgs): FloatingSlots => {
  const headerEnd =
    p.isComputable && p.showHeaderRunControl ? (
      <HeaderEndCluster>
        <NodeHeaderRunButton nodeId={p.nodeId} />
      </HeaderEndCluster>
    ) : undefined;
  const floatingActions =
    p.editable && p.isOnlyFloatingHost ? (
      <NodeFloatingActions nodeId={p.nodeId} />
    ) : undefined;
  const branchesSlot =
    p.branches.length > 0 ? (
      <BranchRow branches={p.branches} ports={p.ports} nodeId={p.nodeId} />
    ) : undefined;
  return { headerEnd, floatingActions, branchesSlot };
};

interface TitleNodeArgs {
  nodeId: string;
  fallbackTitle: React.ReactNode;
  renameDefault: string | undefined;
  onRenameCommit: () => void;
}

/**
 * Renders either the static node title or the inline rename input
 * (controlled by {@code renaming}). Extracted so the host hook can
 * stay below Sonar's cognitive-complexity budget — the rename field's
 * own keydown/pointer handlers add several decision points.
 */
const useTitleNode = (
  renaming: boolean,
  setRenaming: (v: boolean) => void,
  renameRef: React.RefObject<HTMLInputElement | null>,
  { nodeId, fallbackTitle, renameDefault, onRenameCommit }: TitleNodeArgs,
): React.ReactNode => {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        (e.target as HTMLInputElement).blur();
        return;
      }
      if (e.key === 'Escape') setRenaming(false);
    },
    [setRenaming],
  );
  const stop = useCallback(
    (e: React.SyntheticEvent) => e.stopPropagation(),
    [],
  );

  if (!renaming) return fallbackTitle;
  return (
    <RenameInput
      ref={renameRef}
      defaultValue={renameDefault}
      onBlur={onRenameCommit}
      onKeyDown={onKeyDown}
      onPointerDown={stop}
      onClick={stop}
      className="nodrag"
      data-testid={`process-node__rename-input-${nodeId}`}
    />
  );
};

/**
 * Body slot for image_gen — bypasses the generic schema renderer so the
 * dedicated hero slot + resize handle live next to its own controls.
 */
const renderImageGenBody = (p: ImageGenBodyParams): React.ReactNode => (
  <ImageGenNodeBody
    nodeId={p.nodeId}
    instruction={p.instruction}
    onInstructionChange={p.onInstructionChange}
    latestArtifacts={p.latestArtifacts}
    outputPort={p.outputPort}
    heroSize={p.heroSize}
    heroMinSize={p.heroMinSize}
    liveSize={p.liveSize}
    onLiveSizeChange={p.onLiveSizeChange}
    onHeroSizeChange={p.onHeroSizeChange}
    genModel={p.genModel}
    editModel={p.editModel}
    onModelChange={p.onModelChange}
    readOnly={p.readOnly}
  />
);

/**
 * Filter the org-members list down to the picker source: drop expired
 * and banned users (they can't authenticate, so they can't approve a
 * Gate even if assigned) and project each onto the picker's shape.
 *
 * Tolerates older cached responses that omit the flags by treating
 * undefined as "active".
 */
const usePeoplePickerSource = (
  hasPeoplePicker: boolean,
  orgMembers: ReadonlyArray<{
    id: string;
    email: string;
    displayName: string;
    expired?: boolean;
    banned?: boolean;
  }> | undefined,
): readonly PeoplePickerPerson[] =>
  useMemo(() => {
    if (!hasPeoplePicker || !orgMembers) return [];
    return orgMembers
      .filter((m) => m.expired !== true && m.banned !== true)
      .map((m) => ({ id: m.id, name: m.displayName || m.email }));
  }, [hasPeoplePicker, orgMembers]);

/** Project active org groups onto the group-picker's option shape. */
const useGroupPickerSource = (
  hasGroupPicker: boolean,
  orgGroups: ReadonlyArray<{ id: string; name: string }> | undefined,
): readonly { id: string; name: string }[] =>
  useMemo(() => {
    if (!hasGroupPicker || !orgGroups) return [];
    return orgGroups.map((g) => ({ id: g.id, name: g.name }));
  }, [hasGroupPicker, orgGroups]);

/**
 * Bucket live artifacts by their declared output portId, dropping
 * orphans (artifacts with no portId, or whose portId no longer exists
 * on the node). Returns an empty map when the node doesn't render
 * an OutputRow — that path falls back to the orphan-artifacts panel.
 */
const useArtifactsByPortId = (
  showOutputRow: boolean,
  liveArtifacts: readonly ProcessArtifactDto[],
  outputPortIds: ReadonlySet<string>,
): ReadonlyMap<string, readonly ProcessArtifactDto[]> =>
  useMemo(() => {
    if (!showOutputRow) return new Map();
    const map = new Map<string, ProcessArtifactDto[]>();
    for (const a of liveArtifacts) {
      if (!a.portId || !outputPortIds.has(a.portId)) continue;
      const bucket = map.get(a.portId);
      if (bucket) bucket.push(a);
      else map.set(a.portId, [a]);
    }
    return map;
  }, [showOutputRow, liveArtifacts, outputPortIds]);

/**
 * Body slot for every other node kind — schema-driven config fields
 * followed by the orphan-artifacts panel (which only renders artifacts
 * NOT bound to a declared output port, so OutputRow owns the rest).
 *
 * <p>{@code dataBag} is forwarded as {@code siblingValues} so each
 * field can evaluate its own {@code visibleWhen} predicate against
 * the LIVE config blob (e.g. the AUTO-Gate reviewer agent + max
 * iterations only render when {@code mode === 'AUTO'}). Without the
 * sibling map any field that declares a predicate is hidden by
 * default — see {@code isFieldVisible}.</p>
 */
const renderGenericBody = (p: GenericBodyParams): React.ReactNode => {
  const renderField = (field: ConfigFieldDto) => (
    <ConfigFieldRenderer
      key={field.key}
      field={field}
      value={p.dataBag[field.key]}
      onChange={(next) => p.onFieldChange(field.key, next)}
      nodeId={p.nodeId}
      readOnly={p.readOnly}
      people={p.people}
      groups={p.groups}
      groupsLoaded={p.groupsLoaded}
      siblingValues={p.dataBag}
    />
  );

  const execKeys = p.executionSectionKeys;
  const inlineFields = execKeys
    ? p.fields.filter((f) => !execKeys.has(f.key))
    : p.fields;
  const executionFields = execKeys
    ? p.fields.filter((f) => execKeys.has(f.key))
    : [];

  const artifacts = (
    <ArtifactsPanel
      nodeId={p.nodeId}
      latestArtifacts={p.latestArtifacts}
      variant={p.artifactsStyle}
      portIdsToExclude={p.showOutputRow ? p.outputPortIds : undefined}
      generationPreview={p.generationPreview}
    />
  );

  // No execution grouping (non-task kinds): flat list, unchanged.
  if (!execKeys) {
    return (
      <>
        {inlineFields.map(renderField)}
        {p.extraSlot}
        {artifacts}
      </>
    );
  }

  return (
    <>
      {inlineFields.map(renderField)}
      {executionFields.length > 0 && (
        <NodeSection
          title="Execution"
          subtitle={p.executionSubtitle}
          dataTestId="node-execution-section"
        >
          {executionFields.map(renderField)}
          {p.extraSlot}
        </NodeSection>
      )}
      {artifacts}
    </>
  );
};

type DerivedNodeState = ReturnType<typeof useProcessNodeDerivedState>;

interface BodyInnerParams {
  d: DerivedNodeState;
  fields: readonly ConfigFieldDto[];
  sheetExpanded: boolean;
  isImageGen: boolean;
  onFieldChange: (key: string, value: unknown) => void;
  heroSize: HeroSize | null;
  heroMinSize: HeroSize | null;
  liveSize: HeroSize | null;
  setLiveSize: (size: HeroSize | null) => void;
  onHeroSizeChange: (size: HeroSize, minSize: HeroSize) => void;
  people: readonly PeoplePickerPerson[];
  groups: readonly { id: string; name: string }[];
  groupsLoaded: boolean;
  outputPortIds: ReadonlySet<string>;
  capabilityHint: React.ReactNode;
}

const executionSubtitleFor = (
  kind: string,
  runnerTarget: unknown,
): 'LOCAL' | 'CLOUD' | undefined => {
  if (kind !== 'task') return undefined;
  return runnerTarget === 'LOCAL' ? 'LOCAL' : 'CLOUD';
};

const buildBodyInner = (p: BodyInnerParams): React.ReactNode => {
  if (p.sheetExpanded && p.isImageGen) {
    return renderImageGenBody({
      nodeId: p.d.id,
      instruction:
        typeof p.d.dataBag.instruction === 'string' ? p.d.dataBag.instruction : '',
      onInstructionChange: (val) => p.onFieldChange('instruction', val),
      latestArtifacts: p.d.latestArtifacts,
      outputPort: p.d.outputPorts[0],
      heroSize: p.heroSize,
      heroMinSize: p.heroMinSize,
      liveSize: p.liveSize,
      onLiveSizeChange: p.setLiveSize,
      onHeroSizeChange: p.onHeroSizeChange,
      genModel:
        typeof p.d.dataBag.genModel === 'string' ? p.d.dataBag.genModel : undefined,
      editModel:
        typeof p.d.dataBag.editModel === 'string' ? p.d.dataBag.editModel : undefined,
      onModelChange: (field, value) => p.onFieldChange(field, value),
      readOnly: p.d.published,
    });
  }
  if (p.sheetExpanded) {
    return renderGenericBody({
      nodeId: p.d.id,
      fields: p.fields,
      dataBag: p.d.dataBag,
      onFieldChange: p.onFieldChange,
      readOnly: p.d.published,
      people: p.people,
      groups: p.groups,
      groupsLoaded: p.groupsLoaded,
      latestArtifacts: p.d.latestArtifacts,
      artifactsStyle: p.d.artifactsStyle,
      showOutputRow: p.d.showOutputRow,
      outputPortIds: p.outputPortIds,
      generationPreview: p.d.kind === '3d_gen' || p.d.kind === 'image_gen',
      extraSlot: p.capabilityHint,
      executionSectionKeys: p.d.kind === 'task' ? TASK_EXECUTION_FIELD_KEYS : undefined,
      executionSubtitle: executionSubtitleFor(p.d.kind, p.d.dataBag.runnerTarget),
    });
  }
  return null;
}

/**
 * Best-effort capability hint under a LOCAL Task node's assignee picker:
 * does the chosen assignee set own a runner (online or offline) that can
 * run the selected skills? Returns null for cloud nodes, non-Task kinds, or
 * selections that need no installed app. Always calls its hooks so hook order
 * stays stable regardless of the node's current config.
 */
const useTaskCapabilityHint = (d: DerivedNodeState): React.ReactNode => {
  const dataBag = d.dataBag;
  const runnerTarget = dataBag.runnerTarget;
  const isLocalTask = d.kind === 'task' && runnerTarget === 'LOCAL';
  const skillIds = dataBag.skillIds;
  const assigneeType: AssigneeType =
    dataBag.assigneeType === 'GROUP' ? 'GROUP' : 'USER';
  const groupId =
    typeof dataBag.assigneeGroupId === 'string' ? dataBag.assigneeGroupId : undefined;

  const requiredApps = useMemo<string[]>(
    () =>
      isLocalTask
        ? requiredAppsForSkills(d.definition?.agentRequiredApps, skillIds)
        : [],
    [isLocalTask, d.definition, skillIds],
  );

  const userIds = useMemo<string[]>(
    () =>
      Array.isArray(dataBag.assigneeIds)
        ? dataBag.assigneeIds.filter((v): v is string => typeof v === 'string')
        : [],
    [dataBag.assigneeIds],
  );

  const coverage = useCapabilityCoverage({
    apps: requiredApps,
    userIds: assigneeType === 'USER' ? userIds : undefined,
    groupId: assigneeType === 'GROUP' ? groupId : undefined,
  });

  const skillLabel = useMemo(() => {
    const opts = resolveTaskSkillOptions(d.definition, runnerTarget);
    return stringList(skillIds)
      .map((id) => opts?.find((o) => o.value === id)?.label)
      .filter((label) => label !== undefined)
      .join(', ');
  }, [d.definition, runnerTarget, skillIds]);

  if (!isLocalTask || requiredApps.length === 0) return null;
  return (
    <CapabilityHint
      coverage={coverage.data}
      assigneeType={assigneeType}
      agentLabel={skillLabel}
      nodeId={d.id}
    />
  );
};

export function useProcessNodeViewModel(
  props: NodeProps<ProcessNodeRfType>,
) {
  const d = useProcessNodeDerivedState(props);
  const {
    onFieldChange,
    onRename,
    onAddOutput,
    onRemoveOutput,
    onRenameOutput,
    onUpdateOutputInstruction,
    onUpdateOutputPortType,
  } = useProcessNodeActionHandlers({
    id: d.id,
    published: d.published,
    ports: d.ports,
    dataBag: d.dataBag,
    updateNodeData: d.updateNodeData,
    definition: d.definition,
  });

  const [renaming, setRenaming] = useState(false);
  const renameRef = useRef<HTMLInputElement>(null);
  const onRenameRequest = useCallback(() => {
    setRenaming(true);
    requestAnimationFrame(() => renameRef.current?.select());
  }, []);
  const onRenameCommit = useCallback(() => {
    setRenaming(false);
    const raw = renameRef.current?.value ?? '';
    onRename(raw);
  }, [onRename]);

  const titleNode = useTitleNode(renaming, setRenaming, renameRef, {
    nodeId: d.id,
    fallbackTitle: d.title,
    renameDefault: d.persistedLabel ?? d.catalogTitle,
    onRenameCommit,
  });

  const editable = d.published === false;
  const { selectedNodeIds } = useEditorContext();
  const isOnlyFloatingHost =
    selectedNodeIds.length < 2 || selectedNodeIds[0] === d.id;
  const { headerEnd, floatingActions, branchesSlot } = buildFloatingSlots({
    nodeId: d.id,
    isComputable: d.isComputable,
    showHeaderRunControl: d.kind !== 'input',
    editable,
    isOnlyFloatingHost,
    branches: d.branches,
    ports: d.ports,
  });
  // Inline: NodeQuickMenu wires the rename-ref callback, which the
  // react-hooks/refs rule won't allow us to thread through a helper.
  const actions =
    d.isComputable && editable ? (
      <NodeQuickMenu nodeId={d.id} onRenameRequest={onRenameRequest} />
    ) : undefined;

  const hasPeoplePicker = d.fields.some((f) => f.type === 'PEOPLE_PICKER');
  const { data: orgMembers } = useOrgMembers();
  const people = usePeoplePickerSource(hasPeoplePicker, orgMembers);

  const hasGroupPicker = d.fields.some((f) => f.type === 'GROUP_PICKER');
  const { data: orgGroups } = useGroups();
  const groups = useGroupPickerSource(hasGroupPicker, orgGroups);
  const groupsLoaded = orgGroups !== undefined;

  // The skill picker is coupled to the execution target: its options follow the
  // CLOUD/LOCAL catalog, and a flip drops the skills the new one lacks.
  const skillIdsValue = d.dataBag.skillIds;
  const resolvedFields = useMemo(
    () =>
      applyTaskSkillOptions(
        d.fields,
        resolveTaskSkillOptions(d.definition, d.dataBag.runnerTarget),
        stringList(skillIdsValue),
      ),
    [d.fields, d.definition, d.dataBag.runnerTarget, skillIdsValue],
  );
  const definition = d.definition;
  const updateNodeDataFn = d.updateNodeData;
  const nodeIdForCoupling = d.id;
  const onFieldChangeCoupled = useCallback(
    (key: string, next: unknown) => {
      const retained =
        key === 'runnerTarget'
          ? retainSkillIdsForTarget(definition, next, stringList(skillIdsValue))
          : null;
      if (retained) {
        updateNodeDataFn(nodeIdForCoupling, {
          runnerTarget: next,
          skillIds: retained,
        } as Partial<ProcessNodeRfType['data']>);
        return;
      }
      onFieldChange(key, next);
    },
    [onFieldChange, definition, skillIdsValue, updateNodeDataFn, nodeIdForCoupling],
  );

  // Live artifacts → per-port buckets. {@link useArtifactsByPortId}
  // bucketises by {@code portId}; the orphan set (no portId, or a
  // portId that no longer exists) is surfaced via the
  // {@code portIdsToExclude} filter on the top {@link ArtifactsPanel}.
  const liveArtifacts = useNodeLiveArtifacts(d.id, d.latestArtifacts);
  const outputPortIds = useMemo(
    () => new Set(d.outputPorts.map((p) => p.portId)),
    [d.outputPorts],
  );
  const artifactsByPortId = useArtifactsByPortId(
    d.showOutputRow,
    liveArtifacts,
    outputPortIds,
  );

  const sheetExpanded = d.collapsed === false;
  const isImageGen = d.kind === 'image_gen';

  // ImageGen resize state — heroSize/heroMinSize are persisted on
  // dataBag; liveSize is a transient drag-only override owned here (not
  // in the body) so the card width can grow in sync with the slot.
  const heroSize = useHeroSizeMemo(d.dataBag.heroSize);
  const heroMinSize = useHeroSizeMemo(d.dataBag.heroMinSize);
  const [liveSize, setLiveSize] = useState<HeroSize | null>(null);

  // Pin updateNodeData/id so onHeroSizeChange identity stays stable across
  // renders — otherwise the body's useCallback deps would churn.
  const updateNodeData = d.updateNodeData;
  const nodeId = d.id;
  const onHeroSizeChange = useCallback(
    (size: HeroSize, minSize: HeroSize) => {
      updateNodeData(nodeId, { heroSize: size, heroMinSize: minSize });
    },
    [updateNodeData, nodeId],
  );

  const imageGenNodeWidth = computeImageGenNodeWidth(
    isImageGen,
    liveSize,
    heroSize,
  );

  const capabilityHint = useTaskCapabilityHint(d);

  const bodyInner = buildBodyInner({
    d,
    fields: resolvedFields,
    sheetExpanded,
    isImageGen,
    onFieldChange: onFieldChangeCoupled,
    heroSize,
    heroMinSize,
    liveSize,
    setLiveSize,
    onHeroSizeChange,
    people,
    groups,
    groupsLoaded,
    outputPortIds,
    capabilityHint,
  });

  const outputKindOptions = resolveOutputKindOptions(
    d.definition,
    skillIdsValue,
  );

  const runStats =
    RUN_STATS_KINDS.has(d.kind) && sheetExpanded
      ? resolveNodeRunStats(d.runState)
      : null;
  const runStatsSlot = runStats ? (
    <RunStatsRow data-testid={`process-node__run-stats-${d.id}`}>
      {runStats.duration && <span>{runStats.duration}</span>}
      {runStats.credits && <span>{runStats.credits} credits</span>}
    </RunStatsRow>
  ) : null;

  const outputRowSlot =
    d.showOutputRow && sheetExpanded ? (
      <OutputRow
        ports={d.outputPorts}
        nodeId={d.id}
        readOnly={d.published}
        outputInstructions={
          d.dataBag.outputInstructions as Record<string, string> | undefined
        }
        artifactsByPortId={artifactsByPortId}
        artifactsVariant={d.artifactsStyle}
        definition={d.definition}
        kindOptions={outputKindOptions}
        showInstructions={d.showOutputInstructions}
        onAdd={onAddOutput}
        onRemove={onRemoveOutput}
        onRename={onRenameOutput}
        onUpdateInstruction={onUpdateOutputInstruction}
        onUpdatePortType={onUpdateOutputPortType}
      />
    ) : null;

  // AUTO gates show "Iteration: x/N" in the card subtitle while the
  // reviewer agent is running. Calculated from the live iteration on
  // {@code runState.live} (0-based, +1 for display) and the persisted
  // {@code maxIterations} on the gate config (defaults to 5 when the
  // row pre-dates the field). Other kinds get no subtitle.
  const subtitle = resolveGateRunSubtitle({
    kind: d.kind,
    mode: d.dataBag.mode,
    runState: d.runState,
    maxIterations: d.dataBag.maxIterations,
  });

  return {
    id: d.id,
    kind: d.kind,
    cardState: d.cardState,
    variant: d.variant,
    titleNode,
    subtitle,
    iconNode: d.iconNode,
    collapsed: d.collapsed,
    onToggleCollapsed: d.onToggleCollapsed,
    headerEnd,
    actions,
    floatingActions,
    dropTarget: d.isDropTarget,
    emphasis: d.emphasis,
    published: d.published,
    branchesSlot,
    dataTestId: `process-node-${d.kind}-${d.id}`,
    bodyInner,
    outputRowSlot,
    runStatsSlot,
    imageGenNodeWidth,
    portHandlesProps: {
      ports: d.ports,
      nodeId: d.id,
      skipOutputs: d.branches.length > 0 || d.showOutputRow || isImageGen,
      collapsedOutputCount:
        d.showOutputRow && d.collapsed ? d.outputPorts.length : 0,
    },
  };
}
